import { usePrivy } from "@privy-io/react-auth";
import { useQuery } from "@tanstack/react-query";
import { env } from "@/utils/envs";
import QueryKey from "@/utils/queryKeys";

export type PointGrantRecipient = {
	userId: string;
	displayName: string | null;
	email: string | null;
	walletAddress: string | null;
	selectedShinkaiIdentity: string | null;
	discordUsername: string | null;
};

export type PointGrantAdminActivityEntry = {
	id: string;
	userId: string;
	points: number;
	source: string | null;
	sourceRef: string | null;
	description: string | null;
	metadata?: Record<string, unknown> | null;
	grantedAt: string;
	createdByUserId: string | null;
	recipient: PointGrantRecipient | null;
};

export type PointGrantAdminActivityResponse = {
	entries: PointGrantAdminActivityEntry[];
	limit: number;
	offset: number;
	total?: number;
};

const normalizeRecipient = (
	payload: Record<string, unknown>,
): PointGrantRecipient | null => {
	const recipientPayload =
		payload.recipient && typeof payload.recipient === "object"
			? (payload.recipient as Record<string, unknown>)
			: payload;

	const userIdRaw =
		recipientPayload.userId ??
		recipientPayload.id ??
		payload.userId ??
		payload.recipientUserId;
	if (typeof userIdRaw !== "string" || userIdRaw.length === 0) {
		return null;
	}

	return {
		userId: userIdRaw,
		displayName:
			typeof recipientPayload.displayName === "string"
				? recipientPayload.displayName
				: typeof payload.recipientDisplayName === "string"
					? payload.recipientDisplayName
					: null,
		email:
			typeof recipientPayload.email === "string"
				? recipientPayload.email
				: typeof payload.recipientEmail === "string"
					? payload.recipientEmail
					: null,
		walletAddress:
			typeof recipientPayload.walletAddress === "string"
				? recipientPayload.walletAddress
				: typeof payload.recipientWalletAddress === "string"
					? payload.recipientWalletAddress
					: null,
		selectedShinkaiIdentity:
			typeof recipientPayload.selectedShinkaiIdentity === "string"
				? recipientPayload.selectedShinkaiIdentity
				: typeof payload.recipientSelectedShinkaiIdentity === "string"
					? payload.recipientSelectedShinkaiIdentity
					: null,
		discordUsername:
			typeof recipientPayload.discordUsername === "string"
				? recipientPayload.discordUsername
				: typeof payload.recipientDiscordUsername === "string"
					? payload.recipientDiscordUsername
					: null,
	};
};

const normalizeEntry = (
	payload: unknown,
	fallbackId: string,
): PointGrantAdminActivityEntry | null => {
	if (!payload || typeof payload !== "object") {
		return null;
	}

	const entry = payload as Record<string, unknown>;
	const userIdRaw = entry.userId ?? entry.recipientUserId;
	const pointsRaw = entry.points;

	if (typeof userIdRaw !== "string" || typeof pointsRaw !== "number") {
		return null;
	}

	const metadataRaw = entry.metadata;
	const metadata =
		metadataRaw &&
		typeof metadataRaw === "object" &&
		!Array.isArray(metadataRaw)
			? (metadataRaw as Record<string, unknown>)
			: null;

	return {
		id:
			typeof entry.id === "string" && entry.id.length > 0
				? entry.id
				: fallbackId,
		userId: userIdRaw,
		points: pointsRaw,
		source: typeof entry.source === "string" ? entry.source : null,
		sourceRef: typeof entry.sourceRef === "string" ? entry.sourceRef : null,
		description:
			typeof entry.description === "string" ? entry.description : null,
		metadata,
		grantedAt:
			typeof entry.grantedAt === "string"
				? entry.grantedAt
				: typeof entry.createdAt === "string"
					? entry.createdAt
					: new Date().toISOString(),
		createdByUserId:
			typeof entry.createdByUserId === "string" ? entry.createdByUserId : null,
		recipient: normalizeRecipient(entry),
	};
};

const normalizeResponse = (
	payload: unknown,
	limit: number,
	offset: number,
): PointGrantAdminActivityResponse => {
	if (Array.isArray(payload)) {
		return {
			entries: payload
				.map((entry, index) => normalizeEntry(entry, `grant-${offset + index}`))
				.filter(
					(entry): entry is PointGrantAdminActivityEntry => entry !== null,
				),
			limit,
			offset,
		};
	}

	if (!payload || typeof payload !== "object") {
		return { entries: [], limit, offset };
	}

	const objectPayload = payload as {
		entries?: unknown[];
		activity?: unknown[];
		activities?: unknown[];
		items?: unknown[];
		grants?: unknown[];
		total?: number;
		limit?: number;
		offset?: number;
	};

	const source =
		objectPayload.entries ??
		objectPayload.activities ??
		objectPayload.activity ??
		objectPayload.items ??
		objectPayload.grants ??
		[];

	const entries = Array.isArray(source)
		? source
				.map((entry, index) => normalizeEntry(entry, `grant-${offset + index}`))
				.filter(
					(entry): entry is PointGrantAdminActivityEntry => entry !== null,
				)
		: [];

	return {
		entries,
		total: objectPayload.total,
		limit: objectPayload.limit ?? limit,
		offset: objectPayload.offset ?? offset,
	};
};

export const usePointGrantsActivity = (options?: {
	enabled?: boolean;
	limit?: number;
	offset?: number;
}) => {
	const { authenticated, ready, getAccessToken } = usePrivy();
	const limit = options?.limit ?? 20;
	const offset = options?.offset ?? 0;

	return useQuery({
		queryKey: [QueryKey.POINT_GRANTS_ACTIVITY, limit, offset],
		queryFn: async (): Promise<PointGrantAdminActivityResponse> => {
			const token = await getAccessToken();
			if (!token) throw new Error("Unable to retrieve access token");

			const response = await fetch(
				`${env.VITE_SHINKAI_WEB_API_URL}/api/rewards/point-grants/activity?limit=${limit}&offset=${offset}`,
				{
					headers: { Authorization: `Bearer ${token}` },
				},
			);

			if (!response.ok) {
				throw new Error(
					`Failed to fetch point grant activity (${response.status})`,
				);
			}

			const payload = (await response.json()) as unknown;
			return normalizeResponse(payload, limit, offset);
		},
		enabled: (options?.enabled ?? true) && ready && authenticated,
		refetchOnWindowFocus: true,
	});
};
